import { useEffect, useState } from "react";
import { Activity, ArrowUpRight, ArrowDownRight } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtInt, fmtNumber, currencySuffix } from "@/lib/format";
import { EmptyState } from "@/components/common/EmptyState";
import { monthOf } from "@/components/common/DataScope";

/** POS strip on the Ops Dashboard: yesterday's invoice-grain KPIs straight from
 *  the POS deep pull. Renders nothing when the day has no deep data yet. */
export default function OpsPosStrip({ businessDate }) {
    const { t, lang } = useI18n();
    const [data, setData] = useState(undefined);
    const [day, setDay] = useState(undefined);

    useEffect(() => {
        let alive = true;
        api.get("/pos-insights/latest")
            .then((r) => { if (alive) setDay(r.data?.date || null); })
            .catch(() => { if (alive) setDay(null); });
        return () => { alive = false; };
    }, [businessDate]);

    useEffect(() => {
        if (day === undefined) return;
        if (!day) { setData(null); return; }
        let alive = true;
        api.get("/pos-insights/overview", { params: { start: day, end: day } })
            .then((r) => { if (alive) setData(r.data?.available ? r.data : null); })
            .catch(() => { if (alive) setData(null); });
        return () => { alive = false; };
    }, [day]);

    if (data === undefined) return <Skeleton className="h-[92px] w-full rounded-2xl" />;
    if (!data) {
        return (
            <Card data-testid="ops-pos-strip" className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
                <CardContent className="p-4">
                    <div className="mb-2.5 flex items-center gap-2">
                        <Activity className="h-4 w-4 text-[color:var(--ink-faint)]" />
                        <h3 className="text-sm font-bold text-[color:var(--ink-muted)]">{t("pi_title")}</h3>
                    </div>
                    <EmptyState compact testId="ops-pos-strip-empty" dataset="pos_branch_daily" month={monthOf(businessDate)} />
                </CardContent>
            </Card>
        );
    }

    const tot = data.totals || {};
    const peak = (data.hourly || []).reduce((m, h) => (!m || h.gross > m.gross ? h : m), null);
    const appsShare = tot.gross ? (tot.apps_total / tot.gross) * 100 : null;
    const d = data.deltas || {};

    const cards = [
        { k: "transactions", label: t("pi_kpi_transactions"), v: fmtInt(tot.transactions, lang), delta: d.transactions },
        { k: "avg-ticket", label: t("pi_kpi_avg_ticket"), v: fmtNumber(tot.avg_ticket, lang, 2), unit: true, delta: d.avg_ticket },
        { k: "peak-hour", label: t("pi_peak_hour"), v: peak ? `${String(peak.hour).padStart(2, "0")}:00` : "—" },
        { k: "apps", label: t("pi_kpi_apps"), v: appsShare === null ? "—" : `${fmtNumber(appsShare, lang, 1)}%`, delta: d.apps_total },
        { k: "waste", label: t("pi_kpi_waste"), v: fmtNumber(tot.waste_total, lang, 2), unit: true },
        { k: "discounts", label: t("pi_kpi_discounts"), v: fmtNumber(tot.discount_total, lang, 2), unit: true },
    ];

    return (
        <Card data-testid="ops-pos-strip" className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
            <CardContent className="p-4">
                <div className="mb-2.5 flex items-center gap-2">
                    <Activity className="h-4 w-4 text-[color:var(--brand-orange)]" />
                    <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("pi_title")}</h3>
                    <span className="text-[11px] text-[color:var(--ink-muted)]">
                        <bdi dir="ltr">{day}</bdi> · {t("pi_vs_prev")}
                    </span>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    {cards.map((c) => {
                        const up = (c.delta ?? 0) >= 0;
                        const Icon = up ? ArrowUpRight : ArrowDownRight;
                        return (
                            <div key={c.k} data-testid={`ops-pos-${c.k}`}
                                className="rounded-xl border border-[color:var(--border-soft)] surface-2 px-3 py-2">
                                <p className="truncate text-[11px] text-[color:var(--ink-muted)]">{c.label}</p>
                                <div className="flex items-baseline gap-1">
                                    <p className="kpi-number text-lg font-extrabold tnum text-[color:var(--ink)]">
                                        <bdi dir="ltr">{c.v}</bdi>
                                    </p>
                                    {c.unit ? <span className="text-[10px] text-[color:var(--ink-muted)]">{currencySuffix(lang)}</span> : null}
                                </div>
                                {c.delta !== null && c.delta !== undefined ? (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold"
                                        style={{ color: up ? "var(--success)" : "var(--danger)" }}>
                                        <Icon className="h-3 w-3" />
                                        <bdi dir="ltr">{fmtNumber(Math.abs(c.delta), lang, 1)}%</bdi>
                                    </span>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
}
